import { Buffer } from "buffer";
import * as docx from "docx";

export const getBooklet = async (
    superTitle: string,
    title: string,
    label: string
) => {
    const paragraphs: docx.Paragraph[] = [];

    const convertChantToImages = async (liturgyItem: Element) => {
        const svgs: SVGSVGElement[] = [];
        liturgyItem?.querySelectorAll("svg").forEach((svg) => svgs.push(svg));

        for (const svg of svgs) {
            const loadImage = async (
                url: string
            ): Promise<HTMLImageElement> => {
                const img = document.createElement("img");
                img.src = url;
                return new Promise((resolve, reject) => {
                    img.onload = () => resolve(img);
                    img.onerror = reject;
                });
            };

            const dataHeader = "data:image/svg+xml;charset=utf-8";
            const encodeAsUTF8 = (s: any) =>
                `${dataHeader},${encodeURIComponent(s)}`;

            const serializeAsXML = (e: any) =>
                new XMLSerializer().serializeToString(e);

            const svgData = encodeAsUTF8(serializeAsXML(svg));

            const img = await loadImage(svgData);

            const canvas = document.createElement("canvas");
            canvas.width = svg.clientWidth * 4;
            canvas.height = svg.clientHeight * 4;
            canvas
                .getContext("2d")
                ?.drawImage(
                    img,
                    0,
                    0,
                    svg.clientWidth * 4,
                    svg.clientHeight * 4
                );

            const dataUrl = canvas.toDataURL(`image/png`, 1.0);

            var data: Buffer = Buffer.from(dataUrl.split(",")[1], "base64");
            paragraphs.push(
                new docx.Paragraph({
                    children: [
                        new docx.ImageRun({
                            type: "png",
                            data,
                            transformation: {
                                width: 448,
                                height:
                                    448 * (svg.clientHeight / svg.clientWidth),
                            },
                        }),
                    ],
                })
            );
        }
    };

    const liturgyItems: Element[] = [];

    paragraphs.push(
        new docx.Paragraph({
            children: [
                new docx.TextRun({
                    text: superTitle,
                    font: "Palatino Linotype",
                    bold: true,
                    size: 22,
                }),
            ],
            alignment: docx.AlignmentType.CENTER,
        }),
        new docx.Paragraph({
            children: [
                new docx.TextRun({
                    text: title,
                    font: "Palatino Linotype",
                    bold: true,
                    size: 28,
                }),
            ],
            alignment: docx.AlignmentType.CENTER,
        }),
        new docx.Paragraph({
            children: [
                new docx.TextRun({
                    text: "",
                    font: "Palatino Linotype",
                    bold: true,
                    size: 5,
                }),
            ],
        })
    );

    document.querySelectorAll(".liturgy-item").forEach((liturgyItem) => {
        liturgyItems.push(liturgyItem);
    });

    for (const liturgyItem of liturgyItems) {
        let foundAnything = false;

        // Extract rubrics
        liturgyItem.querySelectorAll(".rubric").forEach((rubricItem) => {
            foundAnything = true;

            const rubricMarkdown =
                rubricItem.getAttribute("data-content") ?? "";

            let isBold = true;
            paragraphs.push(
                new docx.Paragraph({
                    alignment: docx.AlignmentType.CENTER,
                    children: rubricMarkdown
                        .replace(/_/g, " ")
                        .split("**")
                        .map((boldPart) => {
                            isBold = !isBold;
                            return new docx.TextRun({
                                text: boldPart,
                                font: "Palatino Linotype",
                                bold: isBold,
                                color: "db281b",
                                size: 20,
                            });
                        }),
                })
            );
        });

        // Extract svgs
        const svgChants: Element[] = [];
        liturgyItem
            .querySelectorAll("div:not(.tone-diagram) > .svg-chant")
            .forEach((svgChant) => {
                svgChants.push(svgChant);
            });

        // Extract titles
        const titleElements: Element[] = [];
        liturgyItem.querySelectorAll(".title").forEach((titleElement) => {
            titleElements.push(titleElement);
        });

        for (const item of titleElements) {
            foundAnything = true;

            const title = item.getAttribute("data-content") ?? "";

            paragraphs.push(
                new docx.Paragraph({
                    children: [
                        new docx.TextRun({
                            text: title,
                            font: "Palatino Linotype",
                            bold: true,
                            size: 22,
                        }),
                    ],
                })
            );

            // If there are SVG chants, pop at least one per title, in order to hack supporting the multipart chants
            if (svgChants) {
                console.log("Number of SVG chants", svgChants.length);
                const current = svgChants.shift();

                await convertChantToImages(current!);
            }
        }

        // Extract text blocks
        liturgyItem.querySelectorAll(".text-block").forEach((rubricItem) => {
            foundAnything = true;

            const markdown = rubricItem.getAttribute("data-content") ?? "";

            const parts = markdown.replace(/ +/g, " ").split("**");

            if (parts[0].length <= 2) {
                parts[1] = parts[0] + parts[1];
                parts[0] = "";
            }

            let isBold = true;
            paragraphs.push(
                new docx.Paragraph({
                    children: parts.map((boldPart) => {
                        isBold = !isBold;
                        return new docx.TextRun({
                            text: boldPart,
                            font: "Palatino Linotype",
                            bold: isBold,
                            size: 20,
                        });
                    }),
                })
            );
        });

        // Extract legacy text blocks
        liturgyItem.querySelectorAll(".legacy-text-block").forEach((item) => {
            if (foundAnything) {
                // Hack that adds spacing after Here beginneth
                paragraphs.push(
                    new docx.Paragraph({
                        children: [
                            new docx.TextRun({
                                text: " ",
                                font: "Palatino Linotype",
                                size: 10,
                            }),
                        ],
                    })
                );
            }

            foundAnything = true;

            item.querySelectorAll(".markdown-text-line").forEach((line) => {
                const markdown = line.getAttribute("data-content") ?? "";
                const indent = parseInt(
                    line.getAttribute("data-indent") ?? "0"
                );

                let tabs = "";

                if (indent !== 10) {
                    for (let i = 0; i < indent; i++) {
                        tabs += "\t";
                    }
                }

                let isBold = true;
                paragraphs.push(
                    new docx.Paragraph({
                        alignment:
                            indent === 10
                                ? docx.AlignmentType.RIGHT
                                : docx.AlignmentType.JUSTIFIED,
                        children: [
                            new docx.TextRun({
                                text: tabs,
                            }),
                        ].concat(
                            markdown
                                .replace(/ +/g, " ")
                                .split("**")
                                .map((boldPart) => {
                                    isBold = !isBold;
                                    return new docx.TextRun({
                                        text: boldPart,
                                        font: "Palatino Linotype",
                                        bold: isBold,
                                        size: 20,
                                    });
                                })
                        ),
                        tabStops: [
                            {
                                type: docx.TabStopType.LEFT,
                                position: 300,
                            },
                            {
                                type: docx.TabStopType.LEFT,
                                position: 600,
                            },
                            {
                                type: docx.TabStopType.LEFT,
                                position: 900,
                            },
                        ],
                    })
                );
            });
        });

        // Extract small spacers
        liturgyItem.querySelectorAll(".spacer").forEach((item) => {
            foundAnything = true;

            paragraphs.push(
                new docx.Paragraph({
                    children: [
                        new docx.TextRun({
                            text: " ",
                            font: "Palatino Linotype",
                            size: 20,
                        }),
                    ],
                })
            );
        });

        // Extract big spacers
        liturgyItem.querySelectorAll(".big-spacer").forEach((item) => {
            foundAnything = true;

            paragraphs.push(
                new docx.Paragraph({
                    children: [
                        new docx.TextRun({
                            text: " ",
                            font: "Palatino Linotype",
                            size: 20,
                        }),
                    ],
                })
            );
            paragraphs.push(
                new docx.Paragraph({
                    children: [
                        new docx.TextRun({
                            text: " ",
                            font: "Palatino Linotype",
                            size: 20,
                        }),
                    ],
                })
            );
        });

        // Process remaining chants
        for (const svgChant of svgChants) {
            await convertChantToImages(svgChant);
        }

        // Add blank line for spacing
        if (foundAnything) {
            paragraphs.push(
                new docx.Paragraph({
                    children: [
                        new docx.TextRun({
                            text: " ",
                            font: "Palatino Linotype",
                            size: 10,
                        }),
                    ],
                })
            );
        }
    }

    const doc = new docx.Document({
        sections: [
            {
                properties: {
                    page: {
                        margin: {
                            top: 852,
                            bottom: 852,
                            left: 852,
                            right: 852,
                        },
                        size: {
                            height: 11930,
                            width: 8410,
                        },
                    },
                },
                children: paragraphs,
            },
        ],
    });

    const blob = await docx.Packer.toBlob(doc);

    var link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `${label ?? "liturgy"}.docx`;
    link.click();
};
